import React, { useContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Info, PaintBrushHousehold } from "phosphor-react";
import { isMobile } from "react-device-detect";
import ReactTooltip from "react-tooltip";
import { MapContext } from "context/map";
import { UserContext } from "context/user";
import { addToCart } from "api/Cart";
import PropTypes from "prop-types";
import CartModal from "components/Modal/CartModal";
import InputField from "components/Input";
import Button from "components/Button";
import "./index.scss";

const Rates = ({ numberOfZones, zoneRates, setZoneRates, resolutionLevel }) => {
  const { token } = useContext(UserContext);
  const { state } = useContext(MapContext);
  const { farmId } = useParams();
  const { map, selectedAlgorithm, selectedDate, selectedFields, zonedData } =
    state;
  const [selectedZone, setSelectedZone] = useState(null);
  const [openCart, setOpenCart] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [formattedAlorithm, setFormattedAlgorithm] =
    useState(selectedAlgorithm);

  useEffect(() => {
    if (selectedZone) {
      const zoneColors = {
        2: { 1: "ffe75e", 2: "34ffa1" },
        3: { 1: "ff9966", 2: "b9ff55", 3: "1260ff" },
        4: { 1: "ff9966", 2: "fcff54", 3: "54f174", 4: "1260ff" },
        5: {
          1: "ff9966",
          2: "ffe75e",
          3: "b9ff55",
          4: "34ffa1",
          5: "1260ff",
        },
      };

      const mapContainer = document.querySelector(".mapboxgl-canvas-container");

      mapContainer.classList.remove(
        "cursor-ff9966",
        "cursor-ffe75e",
        "cursor-b9ff55",
        "cursor-34ffa1",
        "cursor-1260ff",
        "cursor-fcff54",
        "cursor-54f174"
      );
      mapContainer.classList.add(
        `cursor-${zoneColors[numberOfZones][selectedZone]}`
      );
    }

    return () => {
      const mapContainer = document.querySelector(".mapboxgl-canvas-container");
      mapContainer?.classList.remove(
        "cursor-ff9966",
        "cursor-ffe75e",
        "cursor-b9ff55",
        "cursor-34ffa1",
        "cursor-1260ff",
        "cursor-fcff54",
        "cursor-54f174"
      );
    };
  });

  useEffect(() => {
    const handleClick = (e) => {
      if (!selectedZone) return;

      // eslint-disable-next-line no-underscore-dangle
      const sourceData = map.getSource("zones")._data;
      const clickedFeature = e.features[0];
      const featureToUpdate = sourceData.features.find((feature) => {
        return feature.id.toString() === clickedFeature.id.toString();
      });
      featureToUpdate.properties.zone = selectedZone;
      map.getSource("zones").setData(sourceData);
    };

    if (isMobile) {
      map.off("touchstart", "zones", handleClick);
      map.on("touchstart", "zones", handleClick);
    } else {
      map.off("click", "zones", handleClick);
      map.on("click", "zones", handleClick);
    }

    return () => {
      if (isMobile) {
        map.off("touchstart", "zones", handleClick);
      } else {
        map.off("click", "zones", handleClick);
      }
    };
  }, [map, selectedZone]);

  const getZoneColor = (zone) => {
    const zoneColors = {
      2: { 1: "#ffe75e", 2: "#34ffa1" },
      3: { 1: "#ff9966", 2: "#b9ff55", 3: "#1260ff" },
      4: { 1: "#ff9966", 2: "#fcff54", 3: "#54f174", 4: "#1260ff" },
      5: {
        1: "#ff9966",
        2: "#ffe75e",
        3: "#b9ff55",
        4: "#34ffa1",
        5: "#1260ff",
      },
    };

    return zoneColors[numberOfZones][zone];
  };

  const getAlgorithm = useCallback((algorithm) => {
    const algortihmList = {
      NDVI: "standard_ndvi",
      NDVI_R: "standard_ndvi-r",
      NDRE: "standard_ndre",
      PCD: "standard_pcd",
      THERMAL: "standard_thermal",
    };

    return algortihmList[algorithm];
  }, []);

  useEffect(() => {
    if (selectedAlgorithm) {
      const formatAlgorithm = getAlgorithm(selectedAlgorithm);
      setFormattedAlgorithm(formatAlgorithm);
    }
  }, [selectedAlgorithm, getAlgorithm]);

  const getZoneTotalArea = useCallback(
    (zoneNumber) => {
      // eslint-disable-next-line no-underscore-dangle
      const sourceData = map.getSource("zones")._data;

      const zoneFeatures = sourceData.features.filter(
        (feature) => feature.properties.zone === zoneNumber
      );
      const totalArea = zoneFeatures.reduce(
        (acc, feature) => acc + feature.properties.area,
        0
      );
      return totalArea.toFixed(1);
    },
    [map]
  );

  const getZoneRate = (zone) => {
    return zoneRates[zone];
  };

  const getZoneRateValue = useCallback((zoneRate) => {
    const rate = ["", null].includes(zoneRate) ? 0 : parseFloat(zoneRate);

    return rate;
  }, []);

  const updateZoneRate = (zone, rate) => {
    if (rate === "" || /^\d+(\.\d*)?$/.test(rate)) {
      setZoneRates({ [zone]: rate });
    }
  };

  const calculateTotalProductNeeded = useCallback(() => {
    const zonePrices = Array.from(
      { length: numberOfZones },
      (_, i) => i + 1
    ).map((zone) => getZoneTotalArea(zone) * getZoneRateValue(zoneRates[zone]));

    const totalProductNeeded = zonePrices.reduce((acc, rate) => acc + rate, 0);

    return totalProductNeeded % 1 === 0
      ? totalProductNeeded
      : totalProductNeeded.toFixed(2);
  }, [getZoneRateValue, getZoneTotalArea, numberOfZones, zoneRates]);

  const convertZoneRateValue = (rate) => {
    return ["", null].includes(rate) ? null : parseFloat(rate);
  };

  const prepareZoneRate = useCallback(() => {
    return Object.fromEntries(
      Object.entries(zoneRates).map(([key, value]) => [
        key,
        convertZoneRateValue(value),
      ])
    );
  }, [zoneRates]);

  const handleAddToCart = useCallback(async () => {
    const zoneWithRates = {
      features: zonedData.features,
      type: zonedData.type,
      rates: prepareZoneRate(),
      total: calculateTotalProductNeeded(),
    };
    setCartLoading(true);
    const query = "item_type=zoning";
    const item = {
      algorithm: formattedAlorithm,
      catalog_entry: selectedDate,
      num_zones: numberOfZones,
      zone_resolution: resolutionLevel,
      farm_id: farmId,
      fields: selectedFields,
      zones: zoneWithRates,
    };

    try {
      const cartItems = await addToCart(item, query, token);
      setCartLoading(false);
      if (cartItems) {
        setOpenCart(!openCart);
      }
    } catch (e) {
      toast.error(e);
    }
  }, [
    zonedData?.features,
    zonedData?.type,
    prepareZoneRate,
    calculateTotalProductNeeded,
    formattedAlorithm,
    selectedDate,
    numberOfZones,
    resolutionLevel,
    farmId,
    selectedFields,
    token,
    openCart,
  ]);

  return (
    <div className="rates-container">
      <ReactTooltip
        className="tooltip"
        place="right"
        type="dark"
        effect="float"
        multiline
      />
      <div className="rates-header">
        <p className="text">Zone</p>
        <p className="text -area">Area (Ha)</p>
        <p className="text -info">
          Rate
          <Info
            size={20}
            weight="fill"
            color="#8a8e99"
            data-tip="Ensure the product units
            you are using match the rate controller requirements.
            Spray units will likely use L/Ha or Gal/acre.
            Fertiliser, lime etc will likely use kg/Ha or lb/acre."
          />
        </p>
      </div>
      <div className="rates-wrapper">
        {Array.from({ length: numberOfZones }, (_, i) => i + 1).map((zone) => (
          <div className="rate-item" key={zone}>
            <div className="name">
              <div
                className="zone-color"
                style={{ backgroundColor: getZoneColor(zone) }}
              />
              Zone {zone}
            </div>
            <div className="area">{getZoneTotalArea(zone)}</div>
            <div className="actions">
              <InputField
                type="text"
                value={getZoneRate(zone)}
                onChange={(event) => {
                  updateZoneRate(zone, event.target.value);
                }}
              />

              <button
                type="button"
                className={`${
                  selectedZone === zone
                    ? "paint-button -active"
                    : "paint-button"
                }`}
                onClick={() => setSelectedZone(zone)}
              >
                <PaintBrushHousehold size={20} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="product-content">
        <h1 className="needed">{calculateTotalProductNeeded()}</h1>
        <div className="label">Total product needed</div>
        <div className="description">
          A shapefile that is compatible with all major variable rate screens
          and controllers will be provided after payment.{" "}
          <a className="link" href="mailto:support@datafarming.com">
            Contact us
          </a>{" "}
          if you experience any technical difficulties.
        </div>
        <Button
          color="black"
          block
          label="Add to cart"
          onClick={() => handleAddToCart()}
          loading={cartLoading}
          disabled={cartLoading}
        />
      </div>
      {openCart && (
        <CartModal isOpen={openCart} onClose={() => setOpenCart(!openCart)} />
      )}
    </div>
  );
};

Rates.defaultProps = {
  numberOfZones: null,
  zoneRates: {},
  resolutionLevel: null,
};

Rates.propTypes = {
  numberOfZones: PropTypes.number,
  zoneRates: PropTypes.objectOf(PropTypes.number),
  setZoneRates: PropTypes.func.isRequired,
  resolutionLevel: PropTypes.number,
};

export default Rates;
